import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Copy, X } from 'lucide-react';

import { cn } from '@/lib/cn';

import { inkOn, monogram, safeHex, safeHttpUrl, type Tone } from './storefrontFormat';

/** The Storefronts screens' muted ink: the house token (#949494) is 3:1 on
 *  white, under AA for the 10 to 12 px labels these screens are full of.
 *  #666666 is 5.7:1. Scoped to these screens (and their dialogs, which
 *  render in a portal), so the rest of the house portal is unchanged. */
export const ADMIN_CONTRAST: CSSProperties = { ['--ink-muted' as string]: '#666666' };

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'bg-sunken text-ink-secondary border-line-subtle',
  accent: 'bg-accent-wash text-accent border-transparent',
  success: 'bg-success-wash text-success border-transparent',
  warning: 'bg-warning-wash text-warning border-transparent',
  danger: 'bg-[#FEF3F2] text-[#B42318] border-transparent',
  info: 'bg-info-wash text-info border-transparent',
};

/** Notices: the house `danger` token is a neutral gray, so refusals and
 *  errors use the amber attention color to stay visible. */
const NOTICE_CLASS: Record<Tone, string> = {
  ...TONE_CLASS,
  danger: 'bg-warning-wash text-ink border-warning',
};

const DOT_CLASS: Record<Tone, string> = {
  neutral: 'bg-ink-muted',
  accent: 'bg-accent',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-[#B42318]',
  info: 'bg-info',
};

export function Pill({
  tone = 'neutral',
  dot = false,
  children,
  className,
}: {
  tone?: Tone;
  dot?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-6 h-22 px-8 border type-label-xs whitespace-nowrap',
        TONE_CLASS[tone],
        className,
      )}
    >
      {dot && <span aria-hidden className={cn('w-6 h-6 shrink-0', DOT_CLASS[tone])} />}
      {children}
    </span>
  );
}

/** Store logo, or its monogram on the store's accent. */
export function StoreMark({
  name,
  logoUrl,
  accent,
  size = 36,
}: {
  name: string;
  logoUrl: string | null | undefined;
  accent: string | null | undefined;
  size?: number;
}) {
  const [failed, setFailed] = useState(false);
  const src = safeHttpUrl(logoUrl);
  useEffect(() => setFailed(false), [src]);
  const fill = safeHex(accent);
  if (src && !failed) {
    return (
      <span
        className="shrink-0 border border-line-subtle bg-white flex items-center justify-center overflow-hidden"
        style={{ width: size, height: size }}
      >
        <img
          src={src}
          alt=""
          className="max-w-full max-h-full object-contain"
          onError={() => setFailed(true)}
        />
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className="shrink-0 flex items-center justify-center font-mono uppercase"
      style={{
        width: size,
        height: size,
        background: fill,
        color: inkOn(fill),
        fontSize: Math.round(size * 0.36),
      }}
    >
      {monogram(name) || '·'}
    </span>
  );
}

export function Panel({
  title,
  aside,
  children,
  className,
  bodyClassName,
}: {
  title: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cn('border border-line-subtle bg-page', className)}>
      <header className="flex items-center justify-between gap-12 px-16 h-44 border-b border-line-subtle bg-raised">
        <h2 className="type-label-sm text-ink-secondary">{title}</h2>
        {aside && <div className="flex items-center gap-8 min-w-0">{aside}</div>}
      </header>
      <div className={cn('p-16', bodyClassName)}>{children}</div>
    </section>
  );
}

export function Field({
  label,
  children,
  className,
}: {
  label: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-6 min-w-0', className)}>
      <dt className="type-label-xs text-ink-muted">{label}</dt>
      <dd className="type-body-sm text-ink break-words">{children}</dd>
    </div>
  );
}

export function Stat({
  label,
  value,
  hint,
  emphasis = false,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  emphasis?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-8 p-14 border border-line-subtle min-w-0',
        emphasis ? 'bg-ink text-white border-ink' : 'bg-raised',
      )}
    >
      <span className={cn('type-label-xs', emphasis ? 'text-white/70' : 'text-ink-muted')}>
        {label}
      </span>
      <span className="type-data-lg tabular-nums truncate">{value}</span>
      {hint && (
        <span className={cn('type-body-sm', emphasis ? 'text-white/70' : 'text-ink-muted')}>
          {hint}
        </span>
      )}
    </div>
  );
}

export function Th({
  children,
  align = 'left',
  className,
}: {
  children?: ReactNode;
  align?: 'left' | 'right';
  className?: string;
}) {
  return (
    <th
      className={cn(
        'px-12 h-36 type-label-xs text-ink-muted font-normal whitespace-nowrap',
        align === 'right' ? 'text-right' : 'text-left',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  align = 'left',
  className,
  colSpan,
}: {
  children?: ReactNode;
  align?: 'left' | 'right';
  className?: string;
  colSpan?: number;
}) {
  return (
    <td
      colSpan={colSpan}
      className={cn(
        'px-12 py-10 type-body-sm align-middle',
        align === 'right' ? 'text-right tabular-nums' : 'text-left',
        className,
      )}
    >
      {children}
    </td>
  );
}

/** Copies `value` and says so for two seconds ("Copied ✓", announced to
 *  screen readers through a polite live region; a blocked clipboard says
 *  so too, the value stays on screen to select by hand). */
export function CopyButton({
  value,
  label = 'Copy',
  className,
}: {
  value: string;
  label?: string;
  className?: string;
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  // A second click restarts the two seconds.
  const [stamp, setStamp] = useState(0);
  useEffect(() => {
    if (state === 'idle') return;
    const t = window.setTimeout(() => setState('idle'), 2000);
    return () => window.clearTimeout(t);
  }, [state, stamp]);
  return (
    <>
      <button
        type="button"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setState('copied');
          } catch {
            setState('failed');
          }
          setStamp((n) => n + 1);
        }}
        className={cn(
          'inline-flex items-center gap-6 h-28 px-10 border type-label-xs whitespace-nowrap transition-colors',
          state === 'copied'
            ? 'border-success text-success bg-success-wash'
            : 'border-line text-ink-secondary hover:text-ink hover:bg-sunken',
          className,
        )}
      >
        {state !== 'copied' && <Copy size={12} aria-hidden />}
        {state === 'copied' ? 'Copied ✓' : state === 'failed' ? 'Copy blocked' : label}
      </button>
      <span className="sr-only" aria-live="polite" role="status">
        {state === 'copied'
          ? 'Copied to the clipboard'
          : state === 'failed'
            ? 'The browser blocked copying. Select the text by hand.'
            : ''}
      </span>
    </>
  );
}

type ButtonTone = 'primary' | 'secondary' | 'danger' | 'ghost';

/** Destructive actions (reject, pause, void). The house `danger` token is
 *  a neutral gray by the patient portal's no-red rule, which made these
 *  read as ordinary buttons on this internal screen. */
const DESTRUCTIVE = 'text-[#B42318] border-[#B42318] hover:bg-[#FEF3F2]';
const DESTRUCTIVE_SOLID = 'bg-[#B42318] text-white border-[#B42318] hover:bg-[#912018]';

const BUTTON_CLASS: Record<ButtonTone, string> = {
  primary: 'bg-ink text-white border-ink hover:bg-[#252525]',
  secondary: 'bg-page text-ink border-line hover:bg-sunken',
  danger: `bg-page ${DESTRUCTIVE}`,
  ghost: 'bg-transparent text-ink-secondary border-transparent hover:text-ink hover:bg-sunken',
};

export function ActionButton({
  tone = 'secondary',
  children,
  className,
  type = 'button',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { tone?: ButtonTone }) {
  return (
    <button
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-8 h-36 px-14 border type-label-sm whitespace-nowrap',
        'focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-40 disabled:cursor-not-allowed',
        BUTTON_CLASS[tone],
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export const inputClass =
  'w-full h-36 px-10 border border-line bg-page type-body-sm text-ink placeholder:text-ink-muted outline-none focus:border-ink';

export const textareaClass =
  'w-full min-h-[8rem] px-10 py-8 border border-line bg-page type-body-sm text-ink placeholder:text-ink-muted outline-none focus:border-ink resize-y';

/**
 * Confirm step for destructive or customer-visible actions. `children`
 * holds any extra inputs (a note, a medical group); `confirmDisabled`
 * keeps the confirm button off until they are valid.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  tone = 'primary',
  pending = false,
  confirmDisabled = false,
  error,
  onConfirm,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  tone?: 'primary' | 'danger';
  pending?: boolean;
  confirmDisabled?: boolean;
  error?: string | null;
  onConfirm: () => void | Promise<void>;
  children?: ReactNode;
}) {
  // Set synchronously on submit, before React re-renders with `pending`:
  // a double click (or Enter twice) must not send the action twice.
  const inFlight = useRef(false);
  // The control that opened the dialog: focus goes back to it on close
  // (the dialogs open from state, not a Dialog.Trigger, so Radix would
  // otherwise return focus to the page root).
  const opener = useRef<HTMLElement | null>(null);
  return (
    <Dialog.Root open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-dialog bg-black/30" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-dialog flex flex-col w-[calc(100vw-3rem)] max-w-[48rem] max-h-[calc(100dvh-3rem)] -translate-x-1/2 -translate-y-1/2 bg-page border border-line shadow-modal focus:outline-none"
          style={ADMIN_CONTRAST}
          onEscapeKeyDown={(e) => pending && e.preventDefault()}
          onPointerDownOutside={(e) => pending && e.preventDefault()}
          onOpenAutoFocus={() => {
            const active = document.activeElement;
            opener.current = active instanceof HTMLElement && active !== document.body ? active : null;
          }}
          onCloseAutoFocus={(e) => {
            const target = opener.current;
            if (target && target.isConnected) {
              e.preventDefault();
              target.focus();
            }
            opener.current = null;
          }}
        >
          <header className="flex shrink-0 items-center justify-between h-48 pl-20 border-b border-line-subtle bg-raised">
            <Dialog.Title className="type-label-md text-ink">{title}</Dialog.Title>
            <Dialog.Close
              aria-label="Close"
              disabled={pending}
              className="w-48 h-48 flex items-center justify-center border-l border-line-subtle text-ink-secondary hover:text-ink disabled:opacity-40"
            >
              <X size={16} aria-hidden />
            </Dialog.Close>
          </header>
          <form
            className="flex flex-col min-h-0"
            onSubmit={(e) => {
              e.preventDefault();
              if (confirmDisabled || pending || inFlight.current) return;
              inFlight.current = true;
              void Promise.resolve()
                .then(onConfirm)
                .finally(() => {
                  inFlight.current = false;
                });
            }}
          >
            {/* A long dialog (the approve checklist) scrolls inside the
                window; the buttons stay in view. */}
            <div className="flex flex-col gap-16 p-20 min-h-0 overflow-y-auto">
              {description && (
                <Dialog.Description className="type-body-md text-ink-secondary leading-130">
                  {description}
                </Dialog.Description>
              )}
              {children}
              {error && (
                <Notice tone="danger">{error}</Notice>
              )}
            </div>
            <footer className="flex shrink-0 items-center justify-end gap-8 px-20 py-14 border-t border-line-subtle bg-raised">
              <Dialog.Close asChild>
                <ActionButton tone="ghost" disabled={pending}>
                  Cancel
                </ActionButton>
              </Dialog.Close>
              <ActionButton
                type="submit"
                tone={tone === 'danger' ? 'danger' : 'primary'}
                disabled={confirmDisabled || pending}
                className={tone === 'danger' ? DESTRUCTIVE_SOLID : undefined}
              >
                {pending ? 'Working…' : confirmLabel}
              </ActionButton>
            </footer>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('bg-sunken animate-pulse', className)} />;
}

export function Notice({
  tone = 'neutral',
  children,
}: {
  tone?: Tone;
  children: ReactNode;
}) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : undefined}
      className={cn('px-12 py-10 border type-body-sm leading-130', NOTICE_CLASS[tone])}
    >
      {children}
    </div>
  );
}

/** First candidate that loads; `fallback` (or nothing) when none does. */
export function FallbackImage({
  candidates,
  alt,
  className,
  fallback = null,
}: {
  candidates: string[];
  alt: string;
  className?: string;
  fallback?: ReactNode;
}) {
  const key = candidates.join('|');
  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(0), [key]);
  const src = candidates[index];
  if (!src) return <>{fallback}</>;
  return (
    <img
      key={src}
      src={src}
      alt={alt}
      loading="lazy"
      className={className}
      onError={() => setIndex((i) => i + 1)}
    />
  );
}
